import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CookieBlockedMessage from "../../../../../../CommonComponents/CookieBlockedMessage";
import { useAcceptAll } from "../../../../../../../hooks/useCookieChoiceMade";
import { isExternalUrl } from "../../../../../../../utils/cookieConsentUtils";
import "./LightBoxSlideshow.css";

const SWIPE_THRESHOLD = 40;
export const DEFAULT_SLIDE_INTERVAL = 3;

// Viewer-side slideshow for image lightboxes: auto-advances every `intervalSeconds` (default 3).
const LightBoxSlideshow = ({ images, intervalSeconds, dimensions }) => {
    const acceptAll = useAcceptAll();
    const slides = useMemo(() => (
        (images || [])
            .filter(img => img?.element_type_name === 'Image' && (img?.file_url || img?.value?.gif_link))
            // Elements arrive keyed by id, so object order is by id, not the order the author set.
            .sort((a, b) => (a.order_on_page ?? a.id) - (b.order_on_page ?? b.id) || a.id - b.id)
    ), [images]);

    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const touchStartX = useRef(null);
    const total = slides.length;
    const intervalMs = (Number(intervalSeconds) > 0 ? Number(intervalSeconds) : DEFAULT_SLIDE_INTERVAL) * 1000;
    const autoplay = intervalMs > 0 && total > 1;

    const goTo = useCallback((i) => setIndex(((i % total) + total) % total), [total]);
    const next = useCallback(() => goTo(index + 1), [goTo, index]);
    const prev = useCallback(() => goTo(index - 1), [goTo, index]);

    useEffect(() => {
        if (index >= total) setIndex(0);
    }, [total]);

    // Re-armed on every index change so a manual click restarts the full interval.
    useEffect(() => {
        if (!autoplay || paused) return;
        const timer = setTimeout(next, intervalMs);
        return () => clearTimeout(timer);
    }, [autoplay, paused, index, intervalMs, next]);

    // Capture phase + stopPropagation so arrows don't also flip the PDF page behind the lightbox.
    useEffect(() => {
        if (total < 2) return;
        const onKey = (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.stopPropagation();
                e.preventDefault();
                e.key === 'ArrowRight' ? next() : prev();
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [total, next, prev]);

    if (total === 0) return null;

    const width = dimensions?.width || 300;
    const height = dimensions?.height || 300;

    const handleTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) > SWIPE_THRESHOLD) dx < 0 ? next() : prev();
    };

    return (
        <div
            className="lb-slideshow"
            style={{ width, height, maxWidth: '95vw', maxHeight: '90vh' }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={handleTouchEnd}
            role="region"
            aria-roledescription="carousel"
            aria-label="Image slideshow"
        >
            {slides.map((image, i) => {
                const src = image?.file_url ?? image?.value?.gif_link;
                const blocked = !image?.file_url && isExternalUrl(image?.value?.gif_link) && !acceptAll;
                return (
                    <div
                        key={image.id ?? i}
                        className={`lb-slideshow__slide${i === index ? ' is-active' : ''}`}
                        aria-hidden={i !== index}
                        aria-label={`Slide ${i + 1} of ${total}`}
                    >
                        {blocked
                            ? <CookieBlockedMessage contentType="image" style={{ width: '100%', height: '100%' }} />
                            : <img src={src} alt={image?.value?.description || `Slide ${i + 1}`} draggable={false} />}
                    </div>
                );
            })}

            {total > 1 && (
                <>
                    <button type="button" className="lb-slideshow__nav lb-slideshow__nav--prev" onClick={prev} aria-label="Previous slide">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
                    </button>
                    <button type="button" className="lb-slideshow__nav lb-slideshow__nav--next" onClick={next} aria-label="Next slide">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                    </button>

                    <div className="lb-slideshow__counter">{index + 1} / {total}</div>

                    {autoplay && (
                        <button
                            type="button"
                            className="lb-slideshow__toggle"
                            onClick={() => setPaused(p => !p)}
                            aria-label={paused ? 'Play slideshow' : 'Pause slideshow'}
                        >
                            {paused
                                ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" className="fill" /></svg>
                                : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3v14H7zM14 5h3v14h-3z" className="fill" /></svg>}
                        </button>
                    )}

                    <div className="lb-slideshow__dots">
                        {slides.map((s, i) => (
                            <button
                                key={s.id ?? i}
                                type="button"
                                className={`lb-slideshow__dot${i === index ? ' is-active' : ''}`}
                                onClick={() => goTo(i)}
                                aria-label={`Go to slide ${i + 1}`}
                            />
                        ))}
                    </div>

                    {autoplay && (
                        <div
                            key={`${index}-${paused}`}
                            className={`lb-slideshow__progress${paused ? ' is-paused' : ''}`}
                            style={{ animationDuration: `${intervalMs}ms` }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default LightBoxSlideshow;
